/**
 * The lanes' "Site" row: what held the whole site, not one robot.
 *
 * Maintenance windows (blocked time: nothing is dispatched) and fire
 * events (alarms and drills: everything stops). Both are drawn on the row
 * above the robots, on every view, and are never filtered away -- they
 * are the reason a gap in the lanes below exists. Pure.
 *
 * Lift rules on the calendar draw here too, with the lift's name: a block
 * can be why a robot's lane is empty, and an allowed time why it isn't. A
 * lift rule holds nothing at the site, so nothing else counts it as held
 * (`isHeld`, `MonthDay.blocked`, the plan check all read `maintenance`).
 */
import type { FireEvent, LiftRuleOccurrence, MaintenanceWindow } from '../../../api/v1';
import { WINDOW_BAND_WORDS } from '../../Devices/lift-rules-words';
import { overlaps, type TimeWindow } from './lane-time';

export interface SiteBar {
  id: string;
  kind: 'maintenance' | 'fire' | 'lift';
  startMs: number;
  endMs: number;
  /** What the bar says when there is room, and its tooltip. */
  label: string;
  /** What it says when there is not: "Blocked", "Fire drill". */
  shortLabel: string;
  /** Still open (a fire event nobody has resolved yet). */
  ongoing: boolean;
  maintenance?: MaintenanceWindow;
  fire?: FireEvent;
  /** One run of a lift rule on the calendar. */
  liftRule?: LiftRuleOccurrence;
}

export function fireLabel(e: FireEvent): string {
  if (e.drill) return 'Fire drill';
  return e.trigger_source === 'lift' && e.trigger_detail
    ? `Fire alarm (${e.trigger_detail})`
    : 'Fire alarm';
}

export function buildSiteBars(
  maintenance: readonly MaintenanceWindow[],
  fires: readonly FireEvent[],
  window: TimeWindow,
  nowMs: number,
  liftRules: readonly LiftRuleOccurrence[] = [],
): SiteBar[] {
  const bars: SiteBar[] = [];
  for (const w of maintenance) {
    if (!(w.ends_at_ms > w.starts_at_ms)) continue;
    if (!overlaps({ fromMs: w.starts_at_ms, toMs: w.ends_at_ms }, window)) continue;
    bars.push({
      id: `maint:${w.window_id}`,
      kind: 'maintenance',
      startMs: w.starts_at_ms,
      endMs: w.ends_at_ms,
      label: w.reason ? `Blocked · ${w.reason}` : 'Blocked',
      shortLabel: 'Blocked',
      ongoing: false,
      maintenance: w,
    });
  }
  const seen = new Set<string>();
  for (const e of fires) {
    if (seen.has(e.fire_event_id)) continue;
    seen.add(e.fire_event_id);
    const open = e.resolved_at_ms === null || e.resolved_at_ms === undefined;
    const end = open ? Math.max(nowMs, e.triggered_at_ms + 60_000) : e.resolved_at_ms!;
    if (!overlaps({ fromMs: e.triggered_at_ms, toMs: end }, window)) continue;
    bars.push({
      id: `fire:${e.fire_event_id}`,
      kind: 'fire',
      startMs: e.triggered_at_ms,
      endMs: end,
      label: fireLabel(e),
      shortLabel: e.drill ? 'Fire drill' : 'Fire alarm',
      ongoing: open,
      fire: e,
    });
  }
  for (const o of liftRules) {
    if (!(o.ends_at_ms > o.starts_at_ms)) continue;
    if (!overlaps({ fromMs: o.starts_at_ms, toMs: o.ends_at_ms }, window)) continue;
    const words = WINDOW_BAND_WORDS[o.kind];
    bars.push({
      id: `lift:${o.window_id}:${o.starts_at_ms}`,
      kind: 'lift',
      startMs: o.starts_at_ms,
      endMs: o.ends_at_ms,
      label: `${o.lift} · ${words}${o.reason ? ` · ${o.reason}` : ''}`,
      shortLabel: `${o.lift} ${o.kind === 'block_use' ? 'blocked' : 'travel allowed'}`,
      ongoing: false,
      liftRule: o,
    });
  }
  return bars.sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
}
